
import React from 'react';
import { MappedLeadData } from '../../types';
import { getInitials } from '../../utils/helpers';
import { Card, CardHeader, SectionLabel, Tag, SimpleTabContent } from '../common/Atomic';

interface NetworkTabProps {
  data: MappedLeadData;
}

export const NetworkTab: React.FC<NetworkTabProps> = ({ data }) => {
    const network = data.rawProfileIntel?.network_intelligence;
    const buyingCommittee = network?.relationship_mapping?.buying_committee_estimate;
    const internalConnections = network?.internal_network?.leadership_connections;
    const externalConnections = network?.external_network?.industry_connections;

    // Extract internal network data for detailed display
    const internalNetwork = network?.internal_network || {};

    return (
        <SimpleTabContent>
            <div className="space-y-6">
                {/* Network & Engagement Section */}
                {internalNetwork && Object.keys(internalNetwork).length > 0 && (
                    <Card>
                        <CardHeader
                            title="Network & Engagement"
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>}
                            iconColor="text-[#15FBFF]"
                        />

                        {internalNetwork.network_summary && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                                {internalNetwork.network_summary.total_colleagues_identified && (
                                    <div className="p-4 bg-white/[0.02] rounded-xl">
                                        <SectionLabel label="Total Colleagues" />
                                        <div className="text-white font-bold text-2xl">{internalNetwork.network_summary.total_colleagues_identified}</div>
                                    </div>
                                )}
                                {internalNetwork.network_summary.department_size_estimate && (
                                    <div className="p-4 bg-white/[0.02] rounded-xl">
                                        <SectionLabel label="Department Size" />
                                        <div className="text-white font-bold text-2xl">{internalNetwork.network_summary.department_size_estimate}</div>
                                    </div>
                                )}
                                {internalNetwork.network_summary.org_structure_insight && (
                                    <div className="p-4 bg-white/[0.02] rounded-xl">
                                        <SectionLabel label="Org Structure" />
                                        <div className="text-white font-medium text-sm capitalize">{internalNetwork.network_summary.org_structure_insight}</div>
                                    </div>
                                )}
                                {internalNetwork.network_summary.decision_making_style && (
                                    <div className="p-4 bg-white/[0.02] rounded-xl">
                                        <SectionLabel label="Decision Style" />
                                        <div className="text-white font-medium text-sm capitalize">{internalNetwork.network_summary.decision_making_style}</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {(internalNetwork.department_colleagues || internalNetwork.leadership_connections) && (
                            <div className="mt-4 space-y-4">
                                {internalNetwork.department_colleagues && internalNetwork.department_colleagues.length > 0 && (
                                    <div className="p-4 bg-white/[0.02] rounded-xl">
                                        <SectionLabel label="Department Colleagues" />
                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                            {internalNetwork.department_colleagues.slice(0, 5).map((colleague: any, i: number) => (
                                                <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.02] rounded-lg">
                                                    <div className="flex-1">
                                                        <div className="text-white font-medium text-sm">{colleague.name}</div>
                                                        <div className="text-xs text-gray-400">{colleague.title}</div>
                                                        {colleague.relationship_strength && (
                                                            <div className="mt-1">
                                                                <span className={`text-xs px-2 py-0.5 rounded ${
                                                                    colleague.relationship_strength === 'strong' ? 'bg-emerald-500/10 text-emerald-400' :
                                                                    colleague.relationship_strength === 'moderate' ? 'bg-yellow-500/10 text-yellow-400' :
                                                                    'bg-gray-500/10 text-gray-400'
                                                                }`}>{colleague.relationship_strength} connection</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Buying Committee */}
                    <Card>
                        <CardHeader 
                            title="Buying Committee"
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>}
                            iconColor="text-[#5260FF]"
                        />
                        <div className="space-y-3">
                            {buyingCommittee?.identified_members?.map((member: string, i: number) => (
                                <div key={i} className="flex items-center gap-3 p-3 bg-white/[0.02] rounded-xl">
                                    <div className="w-8 h-8 rounded-full bg-[#5260FF]/10 flex items-center justify-center text-[#5260FF] font-bold text-xs">
                                        {getInitials(member)}
                                    </div>
                                    <span className="text-gray-300 font-medium text-sm">{member}</span>
                                </div>
                            ))}
                             {(!buyingCommittee?.identified_members || buyingCommittee.identified_members.length === 0) && (
                                <p className="text-gray-500 italic text-sm">No direct committee members identified, focusing on lead.</p>
                            )}
                        </div>
                        <div className="mt-6 pt-6">
                             <SectionLabel label="Missing Roles" />
                             <div className="flex flex-wrap gap-2">
                                  {buyingCommittee?.missing_roles?.map((role: string, i: number) => (
                                      <Tag key={i} label={role} color="bg-red-500/10 text-red-400" />
                                  ))}
                             </div>
                        </div>
                    </Card>

                    {/* Connections */}
                    <Card>
                        <CardHeader 
                            title="Connections"
                            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>}
                            iconColor="text-[#15FBFF]"
                        />
                        <div className="space-y-4">
                            {internalConnections?.length > 0 && (
                                <div>
                                    <SectionLabel label="Internal" className="mb-2" />
                                    {internalConnections.map((conn: any, i: number) => (
                                        <div key={i} className="p-3 mb-2 bg-white/[0.02] rounded-xl">
                                            <div className="text-white font-bold text-sm">{conn.name}</div>
                                            <div className="text-xs text-gray-500">{conn.title}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {externalConnections?.length > 0 && (
                                <div>
                                    <SectionLabel label="External" className="mb-2" />
                                    {externalConnections.map((conn: any, i: number) => (
                                        <div key={i} className="p-3 mb-2 bg-white/[0.02] rounded-xl">
                                            <div className="text-white font-bold text-sm">{conn.name}</div>
                                            <div className="text-xs text-gray-500">{conn.title} • {conn.company}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {(!internalConnections?.length && !externalConnections?.length) && (
                                <p className="text-gray-500 italic text-sm">No specific connection data available.</p>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </SimpleTabContent>
    );
};
